import { Image, Layout, Menu } from 'antd'
import { HomeOutlined } from '@ant-design/icons'
import { CIMSModule, CIMSPage, pathify } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import { useEffect, useState } from 'react'
import ProjectLink from 'components/ProjectLink'
import { SideNavProps } from './types'
import { useRouteMatches } from 'hooks/useRouteMatches'
import CIMSLink from 'components/CIMSLink'
import { useImportsExports } from './hooks/useImportsExports'
import { useExternals } from './hooks/useExternals'
import { useCAS } from './hooks/useCAS'
import { useDS } from './hooks/useDS'
import { useFDC } from './hooks/useFDC'
import { useConcrete } from './hooks/useConcrete'
import { usePayItems } from './hooks/usePayItems'
import { useAdmin } from './hooks/useAdmin'
import { useRFI } from './hooks/useRFI'
import { useIssues } from './hooks/useIssues'
import { useIFI } from './hooks/useIFI'
import { useDev } from './hooks/useDev'
import logo from './images/cims.png'
import { useMaterials } from './hooks/useMaterials'
import { useEJ } from './hooks/useEJ'
import { useAuthContext } from '../../../context/AuthContext'

/**
 * @see Login component for more information
 */
export const SideNav: React.FC<SideNavProps> = ({ collapsed }) => {
  const {
    state: { projectGlobalId, navKey },
    dispatch: { updateNavKey, updateProjectGlobalId },
  } = useGlobalState()
  const { user } = useAuthContext()

  const [openKeys, setOpenKeys] = useState<string[]>([])
  const { param } = useRouteMatches()
  useEffect(() => {
    const byPath = CIMSPage.fromPath({
      module: param('module') ?? '',
      page: param('page') ?? '',
    })
    if (byPath && byPath.id !== navKey) updateNavKey(byPath.id)
    if (param('home') === 'home') {
      updateProjectGlobalId(undefined)
      return updateNavKey(CIMSModule.Home.id)
    }
  }, [param, navKey, updateNavKey, updateProjectGlobalId])
  useEffect(() => {
    /**
     * Stuff needed to keep one menu open at a time
     */
    if (navKey === CIMSModule.Home.id) return setOpenKeys([])
    const moduleId = CIMSPage.fromId(navKey)?.module?.id
    setOpenKeys(moduleId ? [moduleId] : [])
  }, [navKey])

  const home = user
    ? {
        key: CIMSModule.Home.id,
        icon: <HomeOutlined />,
        label: projectGlobalId ? (
          <ProjectLink item={CIMSModule.Home}>Home</ProjectLink>
        ) : (
          <CIMSLink to={pathify(CIMSModule.Home.pathname)}>Home</CIMSLink>
        ),
      }
    : null

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      collapsedWidth={0}
      trigger={null}
      width={300}
      style={{ overflowY: 'auto' }}
    >
      <Image src={logo} style={{ padding: 16 }} alt="CIMS" preview={false} />
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={navKey ? [navKey] : []}
        openKeys={openKeys}
        onOpenChange={(keys) => {
          const latest = keys[keys.length - 1]
          setOpenKeys(latest ? [latest.toString()] : [])
        }}
        onSelect={({ key }) => {
          if (key === 'sharepoint' || key === 'photoViewer') return
          updateNavKey(key?.toString() ?? CIMSModule.Home.id)
        }}
        items={[
          ...useExternals(),
          home,
          useDev(),
          useImportsExports(),
          useRFI(),
          useCAS(),
          useDS(),
          useFDC(),
          useEJ(),
          useConcrete(),
          useIssues(),
          useIFI(),
          useMaterials(),
          usePayItems(),
          useAdmin(),
        ]}
      />
    </Layout.Sider>
  )
}
